import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiPatch, apiPost } from "./api";

function fmt(v, digits = 2) {
  const n = Number(v || 0);
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: Number.isInteger(n) ? 0 : Math.min(1, digits),
    maximumFractionDigits: digits,
  });
}
function fmtCeil(v) {
  return Math.ceil(Math.max(0, Number(v || 0))).toLocaleString("fr-FR");
}
function usageLimit(credits, kind) {
  if (!credits?.metered) return "∞";
  const key = kind === "sourcing" ? "sourcing_limit" : "request_limit";
  return Math.max(0, Number(credits?.[key] || 0)).toLocaleString("fr-FR");
}

/**
 * v298 — Compteur partagé d'usage VIQ.AI.
 * Une seule source d'affichage pour la topbar, le Dashboard Pro et les vues
 * abonnement : les données viennent toujours de /api/credits/status.
 */
export function CreditUsagePills({ compact = false, className = "", onBuy = null }) {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      // v310 — toujours relire le compteur partagé côté serveur. Un membre Team
      // ne doit jamais conserver un snapshot local de son propre compte : la
      // source de vérité est le wallet du propriétaire de l'organisation.
      const d = await apiGet(`/api/credits/status?fresh=${Date.now()}`);
      if (seq === loadSeq.current && d?.success) setCredits(d);
    } catch (_) {
      // Un compteur indisponible ne doit jamais casser l'espace utilisateur.
    } finally { if (seq === loadSeq.current) setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const refresh = () => load();
    window.addEventListener("viq-credits-changed", refresh);
    window.addEventListener("focus", refresh);
    const timer = window.setInterval(load, 20000);
    return () => {
      window.removeEventListener("viq-credits-changed", refresh);
      window.removeEventListener("focus", refresh);
      window.clearInterval(timer);
    };
  }, [load]);

  const openPayment = useCallback(() => {
    if (typeof onBuy === "function") return onBuy(credits);
    try { window.dispatchEvent(new Event("viq-open-payment-tab")); } catch (_) {}
  }, [credits, onBuy]);

  const title = useMemo(() => {
    if (!credits) return "Compteur de crédits VIQ.AI";
    if (!credits.metered) return "Essai / administration : les crédits ne sont pas décomptés.";
    const cycle = credits.cycle_end ? ` · cycle jusqu'au ${new Date(credits.cycle_end).toLocaleDateString("fr-FR")}` : "";
    return `Sourcing ${fmt(credits.sourcing_count, 0)}/${usageLimit(credits, "sourcing")} · Requêtes ${fmt(credits.request_count, 0)}/${usageLimit(credits, "request")} · ${fmtCeil(credits.total_remaining_credits)} crédits disponibles${cycle}`;
  }, [credits]);

  if (loading && !credits) {
    return <div className={`viq-credit-usage is-loading ${className}`.trim()} aria-label="Chargement des crédits" />;
  }
  if (!credits) return null;

  return (
    <button type="button" className={`viq-credit-usage${compact ? " compact" : ""} ${className}`.trim()}
      onClick={openPayment} title={title} aria-label={title}>
      <span className="viq-credit-counter">
        <span className="viq-credit-icon" aria-hidden="true">⌕</span>
        <span className="viq-credit-label">Sourcing</span>
        <strong>{fmt(credits.sourcing_count, 0)}<span className="viq-credit-limit">/{usageLimit(credits, "sourcing")}</span></strong>
      </span>
      <span className="viq-credit-sep" aria-hidden="true" />
      <span className="viq-credit-counter">
        <span className="viq-credit-icon" aria-hidden="true">✦</span>
        <span className="viq-credit-label">Requêtes</span>
        <strong>{fmt(credits.request_count, 0)}<span className="viq-credit-limit">/{usageLimit(credits, "request")}</span></strong>
      </span>
      <span className="viq-credit-sep" aria-hidden="true" />
      <span className={`viq-credit-balance${credits.metered && Number(credits.total_remaining_credits) <= 5 ? " low" : ""}`}>
        <strong>{credits.metered ? fmtCeil(credits.total_remaining_credits) : "∞"}</strong>
        <span>cr.</span>
      </span>
    </button>
  );
}

export function CreditBalancePill({ compact = false, className = "" }) {
  const [credits, setCredits] = useState(null);
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const d = await apiGet(`/api/credits/status?fresh=${Date.now()}`);
      if (seq === loadSeq.current && d?.success) setCredits(d);
    } catch (_) {}
  }, []);
  useEffect(() => {
    load();
    // Toujours demander le statut autoritatif : certains endpoints déclenchent
    // l'événement avant la finalisation du débit et un membre Team peut recevoir
    // un détail local incomplet.
    const refresh = () => load();
    window.addEventListener("viq-credits-changed", refresh);
    window.addEventListener("focus", refresh);
    const timer = window.setInterval(load, 15000);
    return () => {
      window.removeEventListener("viq-credits-changed", refresh);
      window.removeEventListener("focus", refresh);
      window.clearInterval(timer);
    };
  }, [load]);
  if (!credits) return null;
  if (!credits.metered) {
    return <span className={`viq-credit-balance-pill unmetered${compact ? " compact" : ""} ${className}`.trim()} title="Crédits non décomptés">∞ cr.</span>;
  }
  const authoritativeUsed = Number(credits.included_used_credits || 0) + Number(credits.purchased_used_credits || 0);
  const loggedUsed = Number(credits.total_used_credits ?? ((Number(credits.sourcing_credits_used)||0) + (Number(credits.request_credits_used)||0)));
  // v313 — comptes Team : le ledger détaillé peut être incomplet alors que le
  // wallet partagé a bien été débité. Toujours afficher le plus autoritatif.
  const used = Math.max(authoritativeUsed, loggedUsed);
  const remaining = Number(credits.total_remaining_credits || 0);
  const title = `${fmt(used)} crédits consommés · ${fmtCeil(remaining)} crédits restants`;
  return (
    <button type="button" className={`viq-credit-balance-pill${compact ? " compact" : ""}${remaining <= 5 ? " low" : ""} ${className}`.trim()}
      title={title} aria-label={title} onClick={() => { try { window.dispatchEvent(new Event("viq-open-payment-tab")); } catch (_) {} }}>
      <span className="viq-credit-balance-desktop"><b>{fmt(used)}</b> cr. consommés <i>·</i> <b>{fmtCeil(remaining)}</b> restants</span>
      <span className="viq-credit-balance-mobile"><b>{fmt(used)}</b>/<b>{fmtCeil(remaining)}</b> cr.</span>
    </button>
  );
}

export function CreditPacksPanel({ credits, onChanged = null }) {
  const [buying, setBuying] = useState(null);
  const [error, setError] = useState("");
  const [autoRecharge, setAutoRecharge] = useState(null);
  const [savingAuto, setSavingAuto] = useState(false);
  const loadAutoRecharge = useCallback(async () => {
    try { const d=await apiGet("/api/credits/auto-recharge"); if(d?.success) setAutoRecharge({...d.settings,can_manage:d.can_manage!==false}); } catch (_) {}
  }, []);
  useEffect(() => { loadAutoRecharge(); }, [loadAutoRecharge]);
  const packs = Array.isArray(credits?.packs) && credits.packs.length
    ? credits.packs
    : [50, 100, 200, 500, 1000].map(c => ({ credits:c, price_euros:c }));

  const buy = async (qty) => {
    setBuying(qty); setError("");
    try {
      const d = await apiPost("/api/credits/checkout", { credits:qty });
      if (!d?.checkout_url) throw new Error("Lien de paiement indisponible.");
      window.location.assign(d.checkout_url);
    } catch (e) {
      setError(e?.detail?.error || e?.message || "Impossible d'acheter les crédits.");
      setBuying(null);
    }
  };

  const saveAuto = async (patch) => {
    if (savingAuto || autoRecharge?.can_manage === false) return;
    setSavingAuto(true); setError("");
    const next={...autoRecharge,...patch};
    try {
      const d=await apiPatch("/api/credits/auto-recharge", { enabled:!!next.enabled, threshold_credits:Number(next.threshold_credits)||10, pack_credits:Number(next.pack_credits)||100 });
      if(!d?.success) throw new Error(d?.error||"Réglage impossible.");
      setAutoRecharge({...d.settings,can_manage:true});
      try { window.dispatchEvent(new Event("viq-auto-recharge-changed")); } catch (_) {}
      onChanged?.();
    } catch(e) { setError(e?.detail?.error||e?.message||"Impossible de modifier la recharge automatique."); }
    finally { setSavingAuto(false); }
  };

  return (
    <div className="viq-credit-packs">
      <div className="viq-credit-pack-summary">
        <div><span>Quota inclus du cycle</span><strong>{fmtCeil(credits?.included_credits)} cr.</strong></div>
        <div><span>Inclus utilisé</span><strong>{fmt(credits?.included_used_credits)} cr.</strong></div>
        <div><span>Crédits achetés</span><strong>{fmt(credits?.purchased_credits)} cr.</strong></div>
        <div className="total"><span>Solde disponible</span><strong>{credits?.metered === false ? "Non décompté" : `${fmtCeil(credits?.total_remaining_credits)} cr.`}</strong></div>
      </div>


      <div className="viq-credit-pack-grid" aria-label="Forfaits de crédits supplémentaires">
        {packs.map(pack => (
          <button type="button" key={pack.credits} disabled={buying != null || !credits?.metered || credits?.can_purchase === false}
            onClick={() => buy(Number(pack.credits))}>
            <span>{pack.credits} crédits</span>
            <strong>{fmt(pack.price_euros, 0)} €</strong>
            <small>{Number(pack.credits) * 2} sourcing · {Number(pack.credits) * 4} requêtes</small>
            <em>{buying === Number(pack.credits) ? "Ouverture…" : "Acheter"}</em>
          </button>
        ))}
      </div>
      {credits?.metered && autoRecharge && <div className="viq-auto-recharge-settings">
        <div className="viq-auto-recharge-copy"><strong>Recharge automatique</strong><span>Évite l'interruption de VIQ.AI et autorise l'auto-sourcing. Le paiement utilise la carte enregistrée du propriétaire du compte.</span></div>
        <label className="viq-auto-recharge-toggle"><input type="checkbox" checked={!!autoRecharge.enabled} disabled={savingAuto||autoRecharge.can_manage===false} onChange={e=>saveAuto({enabled:e.target.checked})}/><span>{autoRecharge.enabled?"Activée":"Désactivée"}</span></label>
        <label>Recharger quand le solde atteint<select value={autoRecharge.threshold_credits||10} disabled={!autoRecharge.enabled||savingAuto||autoRecharge.can_manage===false} onChange={e=>saveAuto({threshold_credits:Number(e.target.value)})}>{[5,10,20,50].map(v=><option key={v} value={v}>{v} crédits</option>)}</select></label>
        <label>Forfait automatique<select value={autoRecharge.pack_credits||100} disabled={!autoRecharge.enabled||savingAuto||autoRecharge.can_manage===false} onChange={e=>saveAuto({pack_credits:Number(e.target.value)})}>{packs.map(p=><option key={p.credits} value={p.credits}>{p.credits} crédits · {fmt(p.price_euros,0)} €</option>)}</select></label>
        {autoRecharge.last_error && <small className="viq-auto-recharge-error">Dernière tentative : {autoRecharge.last_error}</small>}
      </div>}
      {!credits?.metered && <div className="viq-credit-pack-note">Les recharges sont disponibles avec un abonnement VIQ.AI actif.</div>}
      {credits?.metered && credits?.can_purchase === false && <div className="viq-credit-pack-note">Les crédits sont partagés avec votre organisation. Seul le propriétaire du compte peut acheter une recharge.</div>}
      {error && <div className="viq-credit-pack-error">{error}</div>}
    </div>
  );
}
